<template>
  <div>
    <input
      v-bind="$attrs"
      v-model="text"
      type="text"
      class="form-control"
      autocomplete="nope"
      :disabled="disabled"
      @blur="checkText"
    >
    <span
      v-if="!!invalidErrorMessage"
      class="help-block text-red"
    >
      {{ invalidErrorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, WritableComputedRef, watch, ref } from 'vue';
import DOMPurify from 'dompurify';
import he from 'he';

import { removeEmojis } from '@/Utils/util';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  allowEmoji: {
    type: Boolean,
    default: false,
  },
  validate: {
    type: Function,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'update-validation']);

const isValid = ref(true);
const invalidErrorMessage = ref('');

const text: WritableComputedRef<string> = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

watch(text, (value: string) => {
  const cleanedString = props.allowEmoji ? value : removeEmojis(value);
  emit('update:modelValue', he.decode(DOMPurify.sanitize(cleanedString, { ALLOWED_TAGS: [] })));
});

const checkText = () => {
  if (!text.value) {
    isValid.value = true;
    invalidErrorMessage.value = '';
  } else if (props.validate) {
    const { valid, validationError } = props.validate(text.value);
    isValid.value = valid;
    invalidErrorMessage.value = validationError || '';
  }

  emit('update-validation', isValid.value);
};

watch(
  text,
  () => {
    if (!props.disabled) checkText();
  },
  {
    immediate: true,
  },
);
</script>
